import classNames from "classnames";
import { Link, useMatch } from "react-router-dom";

import { RobotoDivider } from "@/shared/components/RobotoDivider";
import { RobotoTypography } from "@/shared/components/RobotoTypography";
import { SystemUserOnly } from "@/shared/components/SystemUserOnly";
import type { FeatureFlagName } from "@/shared/domain/featureFlags/FeatureFlagName";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";

import styles from "./Navigation.module.css";
import { navGroups, NavLink } from "./navigationLinks";

interface NavigationProps {
  /** Callback to be invoked when a navigation link is clicked. */
  onNavigate?: () => void;
}

/**
 * Sidebar navigation: feature links, settings/guides, and an admin block
 * for system users. Links declaring a `featureFlag` are hidden when that
 * flag is off for the current org, mirroring `featureFlagRoute`'s gate.
 */
export function Navigation(props: NavigationProps) {
  return (
    <div>
      <NavList links={navGroups.features} onNavigate={props.onNavigate} />

      <RobotoDivider />

      <NavList
        links={navGroups.actionsAndAgents}
        onNavigate={props.onNavigate}
      />

      <RobotoDivider />

      <NavList
        links={navGroups.settingsAndGuides}
        onNavigate={props.onNavigate}
      />

      <SystemUserOnly>
        <RobotoDivider />
        <NavList links={navGroups.admin} onNavigate={props.onNavigate} />
      </SystemUserOnly>
    </div>
  );
}

interface NavListProps {
  links: NavLink[];
  onNavigate?: () => void;
}

function NavList(props: NavListProps) {
  return (
    <ul className={styles.navList}>
      {props.links.map((link) =>
        link.featureFlag === undefined ? (
          <NavListItem
            key={link.name}
            link={link}
            onNavigate={props.onNavigate}
          />
        ) : (
          <FlagGatedNavListItem
            key={link.name}
            link={link}
            featureFlag={link.featureFlag}
            onNavigate={props.onNavigate}
          />
        ),
      )}
    </ul>
  );
}

/**
 * Renders a link only when its flag is on
 */
function FlagGatedNavListItem(
  props: NavListItemProps & { featureFlag: FeatureFlagName },
) {
  const isEnabled = useCheckFeatureFlag(props.featureFlag);

  return isEnabled ? (
    <NavListItem link={props.link} onNavigate={props.onNavigate} />
  ) : null;
}

interface NavListItemProps {
  link: NavLink;
  onNavigate?: () => void;
}

function NavListItem(props: NavListItemProps) {
  return (
    <li className={styles.navItem}>
      <NavItem {...props.link} onClick={props.onNavigate} />
    </li>
  );
}

/**
 * A drawer navigation link button
 */
function NavItem(props: NavLink & { onClick?: () => void }) {
  const match = useMatch(props.link);
  const isActive = match !== null;

  const isExternal = props.isExternalLink;

  return (
    <Link
      to={props.link}
      target={isExternal ? "_blank" : "_self"}
      rel={isExternal ? "noopener noreferrer" : undefined}
      aria-label={isExternal ? `${props.name} (opens in new tab)` : props.name}
      className={classNames(styles.navLink, {
        [styles.active]: isActive,
      })}
      onClick={props.onClick}
    >
      <props.icon className={styles.navIcon} aria-hidden="true" />
      <RobotoTypography variant="body2" className={styles.linkText}>
        {props.name}
      </RobotoTypography>
    </Link>
  );
}
